import * as Sentry from '@sentry/react';
import Ably from 'ably';

import { TokenManager } from '../../auth/TokenManager';
import AppSettings from '../../constants';

import Message = Ably.Message;

let client: Ably.Realtime | null = null;

const subscriptions: Map<string, ((message: Message) => void) | null> = new Map();

export class AblyService {
    static async connect() {
        const accessToken = await TokenManager.getInstance().getToken();

        if (!accessToken) {
            return;
        }

        client = new Ably.Realtime({
            authUrl: AppSettings.routes.api + '/v1/ably/token-request',
            authMethod: 'POST',
            authHeaders: { Authorization: `Bearer ${accessToken}` },
        });

        client.connection.on('failed', (stateChange) => {
            if (stateChange.reason) {
                Sentry.captureException(stateChange.reason);
            }
        });
    }

    static disconnect() {
        if (this.isConnected()) {
            client?.close();
        }
    }

    static isConnected(): boolean {
        return client?.connection.state === 'connected';
    }

    static subscribe(topic: string, handler: (message: Message) => void) {
        if (subscriptions.get(topic)) {
            this.unsubscribe(topic);
        }

        if (!this.isConnected()) {
            return;
        }

        subscriptions.set(topic, handler);

        const channel = client?.channels.get(topic);
        channel?.subscribe(handler).catch((error: Ably.ErrorInfo) => {
            const reason = channel.errorReason || error;

            if (reason.statusCode === 401 || reason.statusCode === 403) {
                Sentry.captureException(reason);
            }
        });
    }

    static unsubscribe(topic: string) {
        if (!this.isConnected()) {
            return;
        }

        if (client && subscriptions.get(topic)) {
            subscriptions.set(topic, null);

            const channel = client?.channels.get(topic);
            channel?.unsubscribe();
        }
    }

    static getClient(): Ably.Realtime | null {
        return client;
    }
}
